import React, { useState } from 'react';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { RoomCard } from '../components/RoomCard';
import { RoomFilters } from '../components/RoomFilters';
import { BookingModal } from '../components/BookingModal';
import { motion } from 'framer-motion';
import { Sparkles, Grid3x3, LayoutGrid } from 'lucide-react';
interface Room {
  id: string;
  name: string;
  type: string;
  description: string;
  capacity: number;
  size: number;
  price: number;
  images: string[];
  amenities: string[];
  features: string[];
  badge?: string;
}
export function BookRoom() {
  const [selectedType, setSelectedType] = useState<string | null>(null);
  const [capacityRange, setCapacityRange] = useState<[number, number]>([2, 100]);
  const [priceRange, setPriceRange] = useState<[number, number]>([500, 10000]);
  const [selectedRoom, setSelectedRoom] = useState<Room | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  // Mock room data
  const rooms: Room[] = [{
    id: '1',
    name: 'Platinum VIP Suite',
    type: 'VIP Suite',
    description: 'Our most luxurious suite featuring panoramic views, private bar, and dedicated concierge service.',
    capacity: 25,
    size: 1200,
    price: 5000,
    images: ['https://images.unsplash.com/photo-1582719478250-c89cae4dc85b?w=800&h=600&fit=crop', 'https://images.unsplash.com/photo-1566073771259-6a8506099945?w=800&h=600&fit=crop', 'https://images.unsplash.com/photo-1631049307264-da0ec9d70304?w=800&h=600&fit=crop'],
    amenities: ['WiFi', 'Sound System', 'Smart TV', 'Mini Bar'],
    features: ['Private entrance', 'Dedicated host', 'Premium bottles', 'VIP parking'],
    badge: 'Most Popular'
  }, {
    id: '2',
    name: 'Gold Lounge',
    type: 'Private Lounge',
    description: 'Intimate private lounge perfect for exclusive gatherings with premium sound system.',
    capacity: 15,
    size: 800,
    price: 3000,
    images: ['https://images.unsplash.com/photo-1618221195710-dd6b41faaea6?w=800&h=600&fit=crop', 'https://images.unsplash.com/photo-1574643156929-51fa098b0394?w=800&h=600&fit=crop'],
    amenities: ['WiFi', 'Sound System', 'Mini Bar'],
    features: ['Mood lighting', 'Premium seating', 'Bottle service', 'Private DJ booth']
  }, {
    id: '3',
    name: 'Diamond Event Space',
    type: 'Event Space',
    description: 'Spacious event venue ideal for corporate events, celebrations, and large gatherings.',
    capacity: 100,
    size: 2500,
    price: 8000,
    images: ['https://images.unsplash.com/photo-1519167758481-83f29da8c2b0?w=800&h=600&fit=crop', 'https://images.unsplash.com/photo-1540575467063-178a50c2df87?w=800&h=600&fit=crop'],
    amenities: ['WiFi', 'Sound System', 'Smart TV', 'Mini Bar'],
    features: ['Stage setup', 'Full catering', 'AV equipment', 'Event coordinator'],
    badge: 'Best for Events'
  }, {
    id: '4',
    name: 'Silver Suite',
    type: 'VIP Suite',
    description: 'Elegant suite with modern amenities and stunning city views for intimate celebrations.',
    capacity: 12,
    size: 600,
    price: 2500,
    images: ['https://images.unsplash.com/photo-1582719508461-905c673771fd?w=800&h=600&fit=crop'],
    amenities: ['WiFi', 'Sound System', 'Smart TV'],
    features: ['City views', 'Private bar', 'Lounge seating', 'Ambient lighting']
  }, {
    id: '5',
    name: 'Emerald Lounge',
    type: 'Private Lounge',
    description: 'Sophisticated lounge space with premium finishes and exclusive bottle service.',
    capacity: 20,
    size: 900,
    price: 3500,
    images: ['https://images.unsplash.com/photo-1551882547-ff40c63fe5fa?w=800&h=600&fit=crop'],
    amenities: ['WiFi', 'Sound System', 'Mini Bar'],
    features: ['VIP entrance', 'Premium bottles', 'Dedicated server', 'Custom playlist']
  }, {
    id: '6',
    name: 'Ruby Penthouse',
    type: 'VIP Suite',
    description: 'Top-floor penthouse suite with rooftop access and unparalleled luxury amenities.',
    capacity: 30,
    size: 1500,
    price: 7000,
    images: ['https://images.unsplash.com/photo-1566073771259-6a8506099945?w=800&h=600&fit=crop'],
    amenities: ['WiFi', 'Sound System', 'Smart TV', 'Mini Bar'],
    features: ['Rooftop access', 'Private elevator', 'Butler service', 'Champagne bar'],
    badge: 'Luxury'
  }];
  const handleBookRoom = (room: Room) => {
    setSelectedRoom(room);
    setIsModalOpen(true);
  };
  const filteredRooms = rooms.filter(room => {
    if (selectedType && selectedType !== 'all' && room.type.toLowerCase().replace(' ', '-') !== selectedType) return false;
    if (room.capacity < capacityRange[0] || room.capacity > capacityRange[1]) return false;
    if (room.price < priceRange[0] || room.price > priceRange[1]) return false;
    return true;
  });
  return <div className="min-h-screen bg-[#0B1014] text-white flex flex-col" data-id="element-721">
      <Header data-id="element-722" />

      {/* Hero Section */}
      <div className="relative pt-32 pb-16 overflow-hidden" data-id="element-723">
        <div className="absolute inset-0 bg-gradient-to-b from-[#DDA247]/5 via-transparent to-transparent" data-id="element-724" />
        <div className="absolute top-20 left-1/3 w-96 h-96 bg-[#DDA247]/10 rounded-full blur-[120px]" data-id="element-725" />

        <div className="container mx-auto px-4 md:px-6 relative z-10" data-id="element-726">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6
        }} className="max-w-3xl mx-auto text-center" data-id="element-727">
            <div className="flex items-center justify-center gap-3 mb-6" data-id="element-728">
              <Sparkles className="w-6 h-6 text-[#DDA247]" data-id="element-729" />
              <span className="text-sm text-[#DDA247] uppercase tracking-[0.2em] font-medium" data-id="element-730">
                Exclusive Spaces
              </span>
              <Sparkles className="w-6 h-6 text-[#DDA247]" data-id="element-731" />
            </div>
            <h1 className="text-4xl md:text-6xl font-bold text-white uppercase tracking-tight mb-6 leading-tight" data-id="element-732">
              Book a Room
            </h1>
            <p className="text-gray-400 text-base md:text-lg leading-relaxed max-w-2xl mx-auto" data-id="element-733">
              Reserve your private space for an unforgettable experience. From
              intimate lounges to grand event spaces, we have the perfect venue
              for every occasion.
            </p>
          </motion.div>
        </div>
      </div>

      {/* Main Content */}
      <main className="flex-1 pb-20 px-4 md:px-6 container mx-auto" data-id="element-734">
        {/* Stats & View Toggle */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.2
      }} className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4" data-id="element-735">
          <div className="flex items-center gap-6" data-id="element-736">
            <div data-id="element-737">
              <span className="text-gray-500 uppercase tracking-wider text-xs" data-id="element-738">
                Available Rooms
              </span>
              <p className="text-2xl font-bold text-white mt-1" data-id="element-739">
                {filteredRooms.length}
              </p>
            </div>
            <div className="h-12 w-px bg-white/10" data-id="element-740" />
            <div data-id="element-741">
              <span className="text-gray-500 uppercase tracking-wider text-xs" data-id="element-742">
                Starting From
              </span>
              <p className="text-2xl font-bold text-[#DDA247] mt-1" data-id="element-743">$2,500</p>
            </div>
          </div>

          {/* View Toggle */}
          <div className="flex bg-[#1A2228] p-1.5 rounded-xl border border-white/10" data-id="element-744">
            <button onClick={() => setViewMode('grid')} className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${viewMode === 'grid' ? 'bg-[#DDA247] text-[#0B1014]' : 'text-gray-400 hover:text-white'}`} data-id="element-745">
              <Grid3x3 size={16} data-id="element-746" />
              <span className="uppercase tracking-wider" data-id="element-747">Grid</span>
            </button>
            <button onClick={() => setViewMode('list')} className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${viewMode === 'list' ? 'bg-[#DDA247] text-[#0B1014]' : 'text-gray-400 hover:text-white'}`} data-id="element-748">
              <LayoutGrid size={16} data-id="element-749" />
              <span className="uppercase tracking-wider" data-id="element-750">List</span>
            </button>
          </div>
        </motion.div>

        {/* Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8" data-id="element-751">
          {/* Filters Sidebar */}
          <motion.div initial={{
          opacity: 0,
          x: -20
        }} animate={{
          opacity: 1,
          x: 0
        }} transition={{
          delay: 0.3
        }} className="lg:col-span-1" data-id="element-752">
            <div className="bg-gradient-to-br from-[#0F161B] to-[#0B1014] border border-white/10 rounded-2xl p-6 sticky top-24" data-id="element-753">
              <RoomFilters selectedType={selectedType} onTypeSelect={setSelectedType} capacityRange={capacityRange} onCapacityChange={setCapacityRange} priceRange={priceRange} onPriceChange={setPriceRange} data-id="element-754" />
            </div>
          </motion.div>

          {/* Rooms Grid */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.4
        }} className="lg:col-span-3" data-id="element-755">
            <div className={`grid gap-6 ${viewMode === 'grid' ? 'grid-cols-1 md:grid-cols-2' : 'grid-cols-1'}`} data-id="element-756">
              {filteredRooms.map((room, index) => <motion.div key={room.id} initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              delay: 0.1 * index
            }} data-id="element-757">
                  <RoomCard room={room} onBook={handleBookRoom} data-id="element-758" />
                </motion.div>)}
            </div>

            {filteredRooms.length === 0 && <div className="text-center py-20" data-id="element-759">
                <p className="text-gray-500 text-lg" data-id="element-760">
                  No rooms match your filters
                </p>
                <button onClick={() => {
              setSelectedType(null);
              setCapacityRange([2, 100]);
              setPriceRange([500, 10000]);
            }} className="mt-4 text-[#DDA247] hover:underline" data-id="element-761">
                  Reset filters
                </button>
              </div>}
          </motion.div>
        </div>
      </main>

      {/* Booking Modal */}
      <BookingModal room={selectedRoom} isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} data-id="element-762" />

      <Footer data-id="element-763" />
    </div>;
}